import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import { GraduationCap, Check, Users, BookOpen, Trophy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const GRADES = [
  { v: "elementary", label: "Elementary (K-5)" },
  { v: "middle", label: "Middle (6-8)" },
  { v: "high", label: "High (9-12)" },
  { v: "mixed", label: "Mixed / multi-grade" },
];

const INTEREST = [
  { v: "committed", label: "Ready to pilot this semester" },
  { v: "interested", label: "Interested — want to learn more" },
  { v: "exploring", label: "Exploring options for next year" },
];

export default function TeacherPilot() {
  const [form, setForm] = useState({
    name: "", school: "", district: "", grade_level: "middle",
    email: "", interest_level: "interested", students_estimate: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, students_estimate: parseInt(form.students_estimate || "0", 10) };
      await axios.post(`${API}/api/teacher-pilots`, payload);
      setSubmitted(true);
      toast.success("Application received — we'll be in touch shortly.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">
      <Seo
        path="/teachers/pilot"
        title="Teacher Pilot Program | Bring WriterSpark to Your Classroom"
        description="Apply for the WriterSpark Teacher Pilot Program — free classroom access for select teachers in exchange for honest feedback. Limited cohort."
        keywords="teacher pilot program, classroom writing platform, teacher beta, writing platform for schools"
      />
      <PublicHeader />
      <main className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <header className="mb-10">
          <span className="text-xs uppercase font-bold tracking-widest text-rose-700 dark:text-rose-400">Teacher Pilot Program</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold leading-tight text-slate-900 dark:text-slate-50" style={{ fontFamily: "Outfit" }}>
            Bring WriterSpark to your classroom — free, this semester.
          </h1>
          <p className="mt-5 text-lg text-slate-600 dark:text-slate-300 max-w-3xl">
            We're hand-picking a small cohort of teachers to pilot WriterSpark with their students. You get free classroom access, hands-on onboarding, and a direct line to our team. In exchange, we'd love your honest feedback and (if you're up for it) a short testimonial.
          </p>
        </header>

        {submitted ? (
          <div data-testid="pilot-thank-you" className="tactile-card p-10 text-center max-w-2xl">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 grid place-items-center mx-auto mb-4">
              <Check size={26} />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-slate-900 dark:text-slate-50" style={{ fontFamily: "Outfit" }}>Application received</h2>
            <p className="text-slate-600 dark:text-slate-400">
              Thanks! We'll review your application within 48 hours and reach out at the email you provided.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_440px] gap-10">
            <section>
              <h2 className="text-xl font-bold mb-4 text-slate-900 dark:text-slate-50" style={{ fontFamily: "Outfit" }}>What you get</h2>
              <ul className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                <Perk icon={Users} title="Free Premium for your whole class" desc="Unlimited essay coaching, SAT/AP prep, revision intelligence — your entire roster." />
                <Perk icon={BookOpen} title="Hands-on onboarding" desc="A 30-minute setup call with our team. We help you build assignment-aligned rubrics." />
                <Perk icon={Trophy} title="Founding Educator status" desc="Logo on our schools page (optional), early access to teacher features, and a permanent Founding Educator badge." />
                <Perk icon={GraduationCap} title="Direct product input" desc="A monthly office hour with the WriterSpark team. Your feedback ships." />
              </ul>
            </section>

            <form onSubmit={submit} className="tactile-card p-6 lg:sticky lg:top-6">
              <h3 className="text-lg font-bold mb-4 text-slate-900 dark:text-slate-50" style={{ fontFamily: "Outfit" }}>Apply for the pilot</h3>
              <F label="Your name" v={form.name} on={(v) => setForm({ ...form, name: v })} testId="pilot-name" required />
              <F label="School" v={form.school} on={(v) => setForm({ ...form, school: v })} testId="pilot-school" required />
              <F label="District (optional)" v={form.district} on={(v) => setForm({ ...form, district: v })} testId="pilot-district" />
              <F label="Work email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testId="pilot-email" required />
              <Select label="Grade level you teach" value={form.grade_level} options={GRADES} on={(v) => setForm({ ...form, grade_level: v })} testId="pilot-grade" />
              <Select label="Interest level" value={form.interest_level} options={INTEREST} on={(v) => setForm({ ...form, interest_level: v })} testId="pilot-interest" />
              <F label="Approx # of students" type="number" v={form.students_estimate} on={(v) => setForm({ ...form, students_estimate: v })} testId="pilot-students" />
              <F label="Anything we should know? (optional)" textarea v={form.notes} on={(v) => setForm({ ...form, notes: v })} testId="pilot-notes" rows={3} />
              <button type="submit" disabled={busy} data-testid="pilot-submit"
                className="mt-3 w-full bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-full py-3 tactile-shadow disabled:opacity-60">
                {busy ? "Submitting…" : "Submit application"}
              </button>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 text-center">We'll respond within 48 hours.</p>
            </form>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

const Perk = ({ icon: Icon, title, desc }) => (
  <li className="flex items-start gap-3">
    <Icon size={18} className="text-rose-600 dark:text-rose-400 mt-0.5 flex-shrink-0" />
    <div><div className="font-bold text-slate-900 dark:text-slate-100">{title}</div><div className="text-sm text-slate-600 dark:text-slate-400">{desc}</div></div>
  </li>
);

const F = ({ label, v, on, type = "text", textarea, rows = 1, testId, required }) => (
  <label className="block mb-3">
    <span className="text-xs uppercase font-bold tracking-widest text-slate-500 dark:text-slate-400">{label}</span>
    {textarea ? (
      <textarea data-testid={testId} rows={rows} value={v} onChange={(e) => on(e.target.value)} required={required}
        className="mt-1 w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:bg-white dark:focus:bg-slate-900 focus:border-rose-400 px-3 py-2 outline-none text-sm text-slate-900 dark:text-slate-100" />
    ) : (
      <input data-testid={testId} type={type} value={v} onChange={(e) => on(e.target.value)} required={required}
        className="mt-1 w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:bg-white dark:focus:bg-slate-900 focus:border-rose-400 px-3 py-2 outline-none text-sm text-slate-900 dark:text-slate-100" />
    )}
  </label>
);

const Select = ({ label, value, options, on, testId }) => (
  <label className="block mb-3">
    <span className="text-xs uppercase font-bold tracking-widest text-slate-500 dark:text-slate-400">{label}</span>
    <select data-testid={testId} value={value} onChange={(e) => on(e.target.value)}
      className="mt-1 w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:bg-white dark:focus:bg-slate-900 focus:border-rose-400 px-3 py-2 outline-none text-sm text-slate-900 dark:text-slate-100">
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  </label>
);
